import { FormControlLabel, FormGroup, Grid, Switch, TextField, Typography } from "@mui/material";
import { useRecordContext } from "ra-core";
import { ChangeEvent, FC, useRef, useState } from "react";
import * as Yup from 'yup';
import { RuleComposeContextProps, RuleComposeContextProvider, RuleDisplayRoot, useRuleComposeController } from "./RuleDisplayComponents";
import { ProtectionRuleDto } from "~/gen/api/securefile";


const validationSchema =
  Yup.object().shape({
    displayName: Yup.string().max(200, 'Rule Name must be at most 200 characters').required('Rule Name is required'),
    comment: Yup.string().max(800, 'Description must be at most 800 characters'),    
  })

interface FormData {
  displayName: string;
  comment: string;
}

interface DataProtectionRuleEditProps {
  fieldProps: (path: keyof FormData, transform?: (x: any) => any) => {
    value: string | null;
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    error: boolean;
    helperText: string | undefined;
  };
  setActive: (a: boolean) => void;
  validateForm: () => Promise<boolean>;
  states: {
    prevValue: FormData&{activeRule: boolean;},
    stage: FormData&{activeRule: boolean;},
    errors: { [K in keyof FormData]+?: string | undefined; };
    touched: { [K in keyof FormData]+?: boolean | undefined; };
  };
  ruleComposeCtrls: RuleComposeContextProps;
  dirty: boolean;
  formDataValid: boolean;
}

export const DataProtectionRuleEdit: FC<DataProtectionRuleEditProps> = ({
  fieldProps, setActive, states, ruleComposeCtrls,
}) => {
  
  return (
    <>
      <Grid container sx={{ py: 2, }} spacing={2} >
        <Grid item xs={5}>
            <TextField label="Rule Name*" fullWidth 
              {...fieldProps('displayName')}
            />
        </Grid>
        <Grid item xs={7}>
          <TextField label="Description" multiline fullWidth 
            {...fieldProps('comment')}
          />
        </Grid>
        <Grid item xs={5}>
          <FormGroup>
            <FormControlLabel 
              control={<Switch color="success" checked={states.stage.activeRule} 
                onChange={(e) => setActive(e.target.checked)} />}
              label="Active"
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Typography variant="subtitle2">Rule Definition</Typography>
      <RuleComposeContextProvider values={ruleComposeCtrls}>
        <RuleDisplayRoot />
      </RuleComposeContextProvider>
    </>
  )
}

export const useDataProtectionRuleEditController = ():DataProtectionRuleEditProps => {
  const record = useRecordContext()
  const dto: ProtectionRuleDto|undefined = record as any
  const ruleComposeCtrls = useRuleComposeController()

  const [states, setStates] = useState(() => {
    const initialValues = {
      displayName: dto?.displayName ?? '',
      comment: dto?.comment ?? '',
      activeRule: dto?.activeRule ?? true,
    }
    return ({
      prevValue: initialValues,
      stage: {...initialValues},
      errors: {} as { [K in keyof(typeof initialValues)]+?: string|undefined },
      touched: {} as { [K in keyof(typeof initialValues)]+?: boolean|undefined },
    })
  })
  
  const validate = (x: any) => {
    try {
      validationSchema.validateSync(x, {abortEarly:false})
    } catch (e: any) {
      if (e.name === 'ValidationError') {
        const errors = {} as Record<string, string>
        for (let err of e.inner) {
          errors[err.path] = err.message
        }
        return errors
      }
    }
  }
  const validateForm = () => {
    return new Promise<boolean>(resolve => {
      setStates(v => {
        v.errors = validate(v.stage) ?? {}
        v.touched = Object.keys(v.prevValue).reduce((acc, k) => {
          acc[k] = true
          return acc
        }, {} as any)
        resolve(Object.values(v.errors).every(x => !x))
        return {...v}
      })
    })
  }
  const timerRef = useRef({} as any)
  const fieldProps = (path: keyof FormData, transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (timerRef.current[path]) {
        window.clearTimeout(timerRef.current[path])
        delete timerRef.current[path]
      }
      const value = transform ? transform(e.target.value) : e.target.value;
      const setValue = (newValue: any) => {
        setStates(v => {
          (v.stage as any)[path] = newValue
          if (v.prevValue[path] !== newValue) {
            v.touched[path] = true
          } else {
            delete v.touched[path]
          }
          v.errors = validate(v.stage) ?? {}
          return {...v}
        })
      }
      setValue(value)
      if (typeof value === 'string' && value.trim() !== value) {
        timerRef.current[path] = setTimeout(() => setValue(value.trim()), 1500)
      }
    },
    error: Boolean(states.touched[path] && states.errors[path]),
    helperText: states.touched[path] ? states.errors[path] : undefined,
  })
  const setActive = (a: boolean) => {
    setStates(v => {
      v.stage.activeRule = a
      if (v.prevValue.activeRule !== v.stage.activeRule) {
        v.touched.activeRule = true
      } else {
        delete v.touched.activeRule
      }
      return {...v}
    })
  }

  return {
    validateForm,
    fieldProps,
    setActive,
    states,
    ruleComposeCtrls,
    dirty: Object.values(states.touched).some(x => x) || ruleComposeCtrls.states.isDirty,
    formDataValid: Object.values(states.errors).every(x => !x),
  }
}
